import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Warehouse } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { useAuth } from "../../utils/auth";
import { canViewFinancials } from "../../utils/roles";
import { fetchE7seblyInventory } from "../../api/E7seblyAPIs";
import { ChartBox, EmptyState, LoadingBlock, SectionCard } from "../Analytics/AnalyticsUi";
import { certaintyLabel, formatCount, formatMoney } from "./e7seblyFormat";

const E7seblyInventoryPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  useEffect(() => {
    if (!authState.token && !authState.isValidating) navigate("/login");
    else if (!authState.isValidating && !canViewFinancials(authState.role)) navigate("/dashboard");
  }, [authState, navigate]);

  const inventory = useQuery({
    queryKey: ["e7sebly-inventory"],
    queryFn: fetchE7seblyInventory,
    enabled: canViewFinancials(authState.role),
  });

  if (!canViewFinancials(authState.role)) return null;
  const data = inventory.data;
  const available = (data?.rows || []).filter((row) => row.countsAsAvailable);

  return (
    <div dir="rtl" className="min-h-screen bg-slate-100 p-3 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-slate-900 text-white"><Warehouse size={18} /></div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold">قيمة المخزون</h1>
              <p className="text-sm text-slate-500">الكمية الحالية × التكلفة القياسية. ليست ربحاً ولا مصروفاً.</p>
            </div>
          </div>
          <Link to="/e7sebly" className="text-sm rounded-xl bg-white border px-3 py-2">العودة إلى احسبلي</Link>
        </div>
        {inventory.isLoading && <LoadingBlock />}
        {inventory.error && <EmptyState message={inventory.error.message} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Summary title="إجمالي الكمية المتاحة" value={formatCount(data.totalAvailableQuantity)} note={data.quantityComplete ? "الكميات المسجّلة مكتملة" : "بعض الكميات ناقصة"} />
              <Summary title="إجمالي القيمة" value={formatMoney(data.totalValue?.amount, data.totalValue?.certainty)} note={data.method} />
              <Summary title="كفرات" value={formatMoney(data.caseValue?.amount, data.caseValue?.certainty)} note={`${formatCount(data.availableCaseQuantity)} قطعة`} />
              <Summary title="جاهز ومسترجع" value={combineStockValue(data.readyMadeValue, data.recoveredValue)} note={`جاهز ${formatCount(data.availableReadyMadeQuantity)} · مسترجع ${formatCount(data.availableRecoveredQuantity)}`} />
            </div>
            <SectionCard title="طريقة التقييم" subtitle={data.definition}>
              <p className="text-sm text-slate-600">محجوز {formatCount(data.reservedQuantity)} · تالف {formatCount(data.damagedQuantity)} · أُعيد استخدامه {formatCount(data.reusedQuantity)}. هذه الكميات خارج المتاح.</p>
            </SectionCard>
            <SectionCard title="المخزون حسب النوع">
              <ChartBox className="h-72">
                <BarChart data={available.filter((row) => row.totalValue != null)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" hide />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="totalValue" name="القيمة" fill="#0f172a" />
                </BarChart>
              </ChartBox>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="p-2 text-right">الصنف</th><th>الفئة</th><th>المصدر</th><th>الكمية</th><th>التكلفة</th><th>القيمة</th><th>اليقين</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map((row) => (
                      <tr key={`${row.category}-${row.source}-${row.name}`} className="border-t">
                        <td className="p-2">{row.name}</td>
                        <td>{row.category}</td>
                        <td>{row.source}</td>
                        <td>{row.quantity == null ? "ناقص" : formatCount(row.quantity)}</td>
                        <td>{row.unitCost == null ? "ناقص" : formatMoney(row.unitCost, "Estimated")}</td>
                        <td>{formatMoney(row.totalValue, row.certainty)}</td>
                        <td>{certaintyLabel[row.certainty]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
            <SectionCard title="سجل القيمة المحفوظ" subtitle="كل نقطة هي قيمة المخزون لحظة حفظ تقرير، وليست رصيداً تاريخياً معاداً بناؤه.">
              {(data.history || []).length === 0 ? <EmptyState message="لا يوجد سجل لأن كمية المخزون التاريخية غير محفوظة" /> : (
                <ul className="text-sm space-y-2">
                  {data.history.map((row) => (
                    <li key={row.name} className="flex justify-between gap-3 border-b py-2">
                      <span>{row.name}<span className="block text-slate-500">{row.detail}</span></span>
                      <span>{formatMoney(row.amount, row.certainty)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
            <SectionCard title="حدود التقييم">
              <ul className="list-disc pr-5 text-sm text-slate-600 space-y-1">
                {(data.limitations || []).map((item) => <li key={item}>{item}</li>)}
              </ul>
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
};

function combineStockValue(left, right) {
  if (left?.amount == null || right?.amount == null) return formatMoney(null, "Incomplete");
  const certainty = left.certainty === "Incomplete" || right.certainty === "Incomplete" ? "Incomplete" : "Estimated";
  return formatMoney(left.amount + right.amount, certainty);
}

function Summary({ title, value, note }) {
  return (
    <div className="bg-white rounded-2xl border p-3">
      <p className="text-sm text-slate-500">{title}</p>
      <p className="mt-2 text-xl font-bold">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{note}</p>
    </div>
  );
}

export default E7seblyInventoryPage;
